import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'About', to: '/about' },
  { label: 'How It Works', to: '/#how' },
  { label: 'Prizes', to: '/#prizes' },
  { label: 'Sponsors', to: '/sponsors' },
  { label: 'Affiliates', to: '/affiliates' },
  { label: 'FAQ', to: '/faq' },
];

const ctaButtons = [
  {
    label: 'Submit Film',
    to: '/submit',
    className:
      'border border-teal text-teal hover:bg-teal hover:text-dark px-5 py-2 rounded-[2px] text-[0.78rem] font-medium tracking-[0.08em] uppercase transition-all',
  },
  {
    label: 'Get Tickets',
    to: '/tickets',
    className:
      'btn-gradient text-dark px-5 py-2 rounded-[2px] text-[0.78rem] font-semibold tracking-[0.08em] uppercase hover:opacity-90 transition-all hover:-translate-y-[1px]',
  },
  {
    label: 'Become a Sponsor',
    to: '/sponsors',
    className:
      'bg-gold text-dark px-5 py-2 rounded-[2px] text-[0.78rem] font-semibold tracking-[0.08em] uppercase hover:opacity-90 transition-all hover:-translate-y-[1px]',
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <nav
      className="fixed top-0 left-0 w-full z-50 transition-colors duration-300"
      style={{
        backgroundColor: scrolled
          ? 'rgba(14,14,18,0.95)'
          : 'rgba(14,14,18,0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(42,191,163,0.15)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="no-underline flex-shrink-0 flex flex-col items-start leading-none">
          <span className="font-title text-4xl tracking-widest nav-logo-gradient">SYNEMA</span>
          <span className="font-display text-[0.5rem] tracking-[0.25em] uppercase" style={{ color: 'rgba(245,245,240,0.45)' }}>Interactive Film Festival</span>
        </Link>

        {/* Center nav links - desktop */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="text-[0.8rem] text-white/60 hover:text-teal transition-colors tracking-[0.12em] uppercase font-medium"
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right CTAs - desktop */}
        <div className="hidden lg:flex items-center gap-3">
          {ctaButtons.map((btn) => (
            <Link key={btn.label} to={btn.to} className={btn.className}>
              {btn.label}
            </Link>
          ))}
        </div>

        {/* Hamburger - mobile */}
        <button
          className="lg:hidden flex flex-col justify-center items-center w-8 h-8 gap-1.5"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          <span
            className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${
              menuOpen ? 'rotate-45 translate-y-2' : ''
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-white transition-opacity duration-300 ${
              menuOpen ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${
              menuOpen ? '-rotate-45 -translate-y-2' : ''
            }`}
          />
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
            className="fixed top-16 right-0 bottom-0 w-72 z-40 lg:hidden flex flex-col px-6 py-8 gap-6 overflow-y-auto"
            style={{
              backgroundColor: 'rgba(14,14,18,0.97)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderLeft: '1px solid rgba(42,191,163,0.15)',
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-base text-light/70 hover:text-white transition-colors tracking-wide"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            <div className="h-px bg-white/10 my-2" />

            {ctaButtons.map((btn) => (
              <Link
                key={btn.label}
                to={btn.to}
                className={`${btn.className} text-center`}
                onClick={() => setMenuOpen(false)}
              >
                {btn.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop overlay for mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-16 bg-black/50 z-30 lg:hidden"
            onClick={() => setMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </nav>
  );
}
